/**
 * Costi personale (CCNL): tariffe orarie costo-azienda per mansione —
 * ordinario, straordinario, notturno, festivo, festivo-notturno, sostituzione
 * esterna. Stesso pattern override/default di AziendaliSection: righe
 * configurate su Dataverse sovrascrivono i default CCNL hardcoded in
 * services/hrCostConfig.js (usati anche come fallback per mansioni non
 * ancora configurate qui).
 */
import React, { useState } from "react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import {
  Card,
  Spinner,
  ErrorBox,
  SimpleTable,
} from "../../components/ui/basics";
import MansioneSelect from "../../components/MansioneSelect";

const CAMPI_COSTO = [
  { key: "costo_ordinario", label: "Ordinario" },
  { key: "costo_straordinario", label: "Straordinario" },
  { key: "costo_notturno", label: "Notturno" },
  { key: "costo_festivo", label: "Festivo" },
  { key: "costo_festivo_notturno", label: "Festivo nott." },
  { key: "costo_sostituzione_esterna", label: "Sostituz. esterna" },
];

const euro = (v) => (v != null ? `€ ${Number(v).toFixed(2)}` : "—");

export default function CostiSection() {
  const { apiRequest } = useAuth();
  const { data, loading, error, reload } = useApi("/costi");
  const { data: effettivi } = useApi("/costi/effettivi");
  const { data: livelli } = useApi("/costi/livelli");
  const { data: status } = useApi("/costi/status");
  const [modal, setModal] = useState(null);

  const writable = status?.available !== false;
  const defaults = (effettivi || []).filter((r) => r.origine === "default");

  const baseColumns = [
    { key: "mansione", label: "Mansione" },
    { key: "livello", label: "Livello" },
    ...CAMPI_COSTO.map((c) => ({
      key: c.key,
      label: c.label,
      align: "right",
      render: (r) => euro(r[c.key]),
    })),
    { key: "note", label: "Note" },
  ];

  const submit = async (values) => {
    const payload = { ...values };
    for (const c of CAMPI_COSTO) {
      const raw = values[c.key];
      payload[c.key] =
        raw !== "" && raw != null
          ? Number(String(raw).replace(",", "."))
          : null;
    }
    if (modal.mode === "create") {
      await apiRequest("/costi", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    } else {
      await apiRequest(`/costi/${modal.record.id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
    }
    reload();
  };

  const elimina = async (r) => {
    if (
      !window.confirm(`Eliminare la configurazione costi per "${r.mansione}"?`)
    )
      return;
    await apiRequest(`/costi/${r.id}`, { method: "DELETE" });
    reload();
  };

  return (
    <Card
      title="Costi personale (CCNL)"
      subtitle="Tariffe orarie costo-azienda per mansione. Le mansioni non configurate qui usano i default CCNL indicativi."
      actions={
        writable && (
          <button
            onClick={() => setModal({ mode: "create" })}
            className="text-sm bg-primary-600 text-white px-3 py-1.5 rounded hover:bg-primary-700"
          >
            + Nuova configurazione
          </button>
        )
      }
    >
      {!writable && (
        <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2 mb-3">
          ⚠ Sola lettura: l'entità Dataverse dei costi personalizzati non è
          ancora stata creata sul tenant (gate schema, richiede approvazione di
          Matteo). Sotto i default CCNL indicativi.
        </p>
      )}
      {loading && <Spinner />}
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data && (
        <SimpleTable
          rowKey={(r) => r.id}
          columns={
            writable
              ? [
                  ...baseColumns,
                  {
                    key: "azioni",
                    label: "",
                    render: (r) => (
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => setModal({ mode: "edit", record: r })}
                          className="text-xs text-primary-600 hover:underline"
                        >
                          Modifica
                        </button>
                        <button
                          onClick={() => elimina(r)}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Elimina
                        </button>
                      </div>
                    ),
                  },
                ]
              : baseColumns
          }
          rows={data}
        />
      )}

      {defaults.length > 0 && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <p className="text-xs text-gray-500 mb-2">
            Da default CCNL (nessuna configurazione specifica):
          </p>
          <SimpleTable
            rowKey={(r) => r.mansione}
            columns={
              writable
                ? [
                    ...baseColumns,
                    {
                      key: "azioni",
                      label: "",
                      render: (r) => (
                        <button
                          onClick={() =>
                            setModal({ mode: "create", record: { ...r } })
                          }
                          className="text-xs text-primary-600 hover:underline"
                        >
                          Sovrascrivi qui
                        </button>
                      ),
                    },
                  ]
                : baseColumns
            }
            rows={defaults}
          />
        </div>
      )}

      {livelli?.length > 0 && (
        <details className="mt-4 text-xs text-gray-500">
          <summary className="cursor-pointer select-none">
            Tabella di riferimento CCNL AIOP RSA per livello
          </summary>
          <div className="mt-2">
            <SimpleTable
              rowKey={(r) => r.livello}
              columns={[
                { key: "livello", label: "Livello" },
                {
                  key: "paga_base_oraria",
                  label: "Paga lorda/h",
                  align: "right",
                  render: (r) => euro(r.paga_base_oraria),
                },
                {
                  key: "costo_azienda_orario",
                  label: "Costo azienda/h",
                  align: "right",
                  render: (r) => euro(r.costo_azienda_orario),
                },
                {
                  key: "qualifiche_tipiche",
                  label: "Qualifiche tipiche",
                  render: (r) => (r.qualifiche_tipiche || []).join(", "),
                },
              ]}
              rows={livelli}
            />
          </div>
        </details>
      )}

      {modal && (
        <CostoModal
          mode={modal.mode}
          record={modal.record}
          onSubmit={submit}
          onClose={() => setModal(null)}
        />
      )}
    </Card>
  );
}

function CostoModal({ mode, record, onSubmit, onClose }) {
  const [values, setValues] = useState(record || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const setField = (key, v) => setValues((s) => ({ ...s, [key]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit(values);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">
            {mode === "create"
              ? "Nuova configurazione costi"
              : `Modifica costi ${values.mansione}`}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <div className="flex gap-2">
            <label className="block text-sm flex-1">
              <span className="block text-xs text-gray-500 mb-1">
                Mansione *
              </span>
              <MansioneSelect
                value={values.mansione}
                onChange={(v) => setField("mansione", v)}
                required
                className="border border-gray-300 rounded px-2 py-1.5 w-full"
              />
            </label>
            <label className="block text-sm w-28">
              <span className="block text-xs text-gray-500 mb-1">
                Livello CCNL
              </span>
              <input
                type="text"
                value={values.livello ?? ""}
                onChange={(e) => setField("livello", e.target.value)}
                placeholder="es. D1"
                className="border border-gray-300 rounded px-2 py-1.5 w-full"
              />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {CAMPI_COSTO.map((c) => (
              <label key={c.key} className="block text-sm">
                <span className="block text-xs text-gray-500 mb-1">
                  {c.label} (€/h){c.key === "costo_ordinario" && " *"}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  required={c.key === "costo_ordinario"}
                  value={values[c.key] ?? ""}
                  onChange={(e) => setField(c.key, e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1.5 w-full"
                />
              </label>
            ))}
          </div>
          <label className="block text-sm">
            <span className="block text-xs text-gray-500 mb-1">Note</span>
            <input
              type="text"
              value={values.note ?? ""}
              onChange={(e) => setField("note", e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 w-full"
            />
          </label>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-primary-600 text-white text-sm px-4 py-1.5 rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
